import { debounce } from 'lodash';
import moment from "moment";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from 'react-infinite-scroll-component';
import { batch, useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { Row } from "reactstrap";
import ChatHeading from "../../../components/applications/ChatHeading";
import MessageCard from "../../../components/applications/MessageCard";
import SaySomething from "../../../components/applications/SaySomething";
import { Colxx } from "../../../components/common/CustomBootstrap";
import ChatApplicationMenu from "../../../containers/applications/ChatApplicationMenu";
import { firestore, functions } from "../../../helpers/Firebase";
import { SearchChats, compareContacts, createNotification } from "../../../helpers/Utils";
import { useIsMobile } from "../../../hooks/use-mobile";
import { deleteChatMessage, getChatMessages, getContacts, setUserFromRequests } from "../../../redux/chat/actions";

const Chat = ({ history, location, match }) => {
  const { user } = useSelector(state => state.authUser);
  const { provider } = useSelector(state => state.providers);
  const { allContacts, messages, userFromRequests, chatsFilter } = useSelector(state => state.chatApp);
  const [messageInput, setMessageInput] = useState('');
  const [lastVisibleChat, setLastVisibleChat] = useState(null);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const dispatch = useDispatch();
  const [searchKey, setSearchKey] = useState('');
  const isMobile = useIsMobile();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get selected user ID from URL params
  const userIdFromUrl = useMemo(() => {
    if (!location || !location.search) {
      console.log('No location or search params');
      return null;
    }
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get('userId');
    console.log('userIdFromUrl:', userId, 'from location.search:', location.search);
    return userId;
  }, [location]);

  // Find selected user from allContacts based on URL param
  const selectedUser = useMemo(() => {
    if (!userIdFromUrl || !allContacts?.length) return null;
    const found = allContacts.find(contact => contact.id === userIdFromUrl);
    console.log('selectedUser:', found?.name || 'not found', 'for userId:', userIdFromUrl);
    return found || null;
  }, [userIdFromUrl, allContacts]);

  // Function to navigate to chat with user ID
  const navigateToUser = useCallback((userId) => {
    console.log('=== navigateToUser called ===');
    console.log('userId:', userId);
    console.log('history exists:', !!history);
    console.log('history.push exists:', !!(history && history.push));
    console.log('location:', location);

    if (!history || !history.push) {
      console.error('History or history.push is not available!');
      return;
    }

    if (userId) {
      const newUrl = `${location.pathname}?userId=${encodeURIComponent(userId)}`;
      console.log('Calling history.push with:', newUrl);
      history.push(newUrl);
      console.log('After history.push, current URL:', window.location.href);
    } else {
      console.log('Clearing URL params');
      history.push(location.pathname);
    }
  }, [history, location]);

  // Update URL when userFromRequests is set (navigation from other pages)
  useEffect(() => {
    if (userFromRequests && allContacts?.length) {
      const userExists = allContacts.find(contact => contact.id === userFromRequests);
      if (userExists && (!userIdFromUrl || userIdFromUrl !== userFromRequests)) {
        navigateToUser(userFromRequests);
      }
    }
  }, [userFromRequests, allContacts, userIdFromUrl, navigateToUser]);

  // Optimize debounce timing - reduced from 1500ms to 300ms for better UX
  const debounce_fun = useRef(
    debounce(async function (e) {
      setSearchKey(e || '')
    }, 300)
  ).current;

  const handleSearchContact = e => {
    debounce_fun(e)
  };
  // Deep link (?userId=<wa_id>) opened in a fresh tab, e.g. "Chat with customer" from Rated Orders:
  // redux has no userFromRequests there, so seed it from the URL once and let the search path
  // below load + select that conversation even when it is outside the first page of contacts.
  const deepLinkSeeded = useRef(false);
  useEffect(() => {
    if (deepLinkSeeded.current || !userIdFromUrl || userFromRequests || !user?.uid) return;
    deepLinkSeeded.current = true;
    dispatch(setUserFromRequests(userIdFromUrl));
    // eslint-disable-next-line
  }, [userIdFromUrl, user?.uid]);

  // Optimize search - combine searchKey and userFromRequests logic
  useEffect(() => {
    const searchText = searchKey || userFromRequests;
    if (!searchText || !user?.uid) return;

    (async () => {
      try {
        let results = await SearchChats({ user, searchText })
        setLastVisibleChat(results[results.length - 1])
        setHasMoreChats(results.length > 0)
        getInitialData(results, true)
      } catch (error) {
        console.error('Error searching chats:', error);
      }
    })()
    // eslint-disable-next-line
  }, [searchKey, userFromRequests, user?.uid]);

  const getInitialData = useMemo(() => {
    return async (snap, fromSearch) => {
      let docs = fromSearch ? snap : snap?.docs;
      if (!docs?.length) {
        batch(() => {
          dispatch(setUserFromRequests(undefined));
          dispatch(getContacts([]));
        });
        // Clear URL param if no contacts
        if (userIdFromUrl) {
          navigateToUser(null);
        }
        return;
      }

      // Batch collect all messages to dispatch at once
      const allMessagesToDispatch = [];

      let data = await Promise.all(docs?.map(async rec => {
        let post = rec.data();
        let ref = firestore().collection('chats').doc(rec.id).collection('messages');
        if (user?.accessType === 'admin') {
          ref = ref.where('provider_id', '==', user?.uid);
        }
        let messagesSnap = await ref.orderBy("created", "desc").limit(10).get();

        // Collect messages instead of dispatching immediately
        let chatMessages = messagesSnap.docs.map((d) => {
          let parent_id = d.ref.parent.parent.id;
          let chatData = { ...d.data(), parent_id };
          allMessagesToDispatch.push(chatData);
          return chatData;
        });

        const lastVisible = messagesSnap.docs[messagesSnap.docs.length - 1];
        let sortedMessages = chatMessages?.sort((msg1, msg2) => msg1?.created - msg2?.created);
        const lastMessage = sortedMessages?.[sortedMessages?.length - 1];
        let exp = post?.exp && !['create_new_session', 'pending_order', 'booking_confirmed']?.includes(lastMessage?.template_name) ? moment(post?.exp).isAfter(moment()) : true;
        return ({ ...post, lastVisible, id: rec.id, isInfiniteLoading: chatMessages.length, exp: !exp, chatMessages });
      }));

      // Batch all Redux dispatches together to minimize re-renders
      batch(() => {
        // Dispatch all messages at once
        allMessagesToDispatch.forEach(chatData => {
          dispatch(getChatMessages(chatData));
        });

        // If there is userFromRequests, navigate to that user's chat
        if (userFromRequests) {
          const userToSelect = data.find(r => r?.id === userFromRequests);
          if (userToSelect && (!selectedUser || selectedUser?.id !== userFromRequests)) {
            navigateToUser(userFromRequests);
          }
          dispatch(setUserFromRequests(undefined));
        }

        dispatch(getContacts(data?.filter(r => r)?.sort(compareContacts)));
      });
    }
  }, [dispatch, user, userFromRequests, selectedUser, userIdFromUrl, navigateToUser]);
  // Store unsubscribe functions in refs to ensure cleanup on unmount
  const unsubscribeMessagesRef = useRef(null);
  const unsubscribeChatsRef = useRef(null);
  // Ref for scrollable messages container
  const scrollableDivRef = useRef(null);
  const previousScrollTopRef = useRef(0);

  // Optimize: Combine message listener with initial data loading
  useEffect(() => {
    let unsubscribeMessages = null;

    // Set up real-time message listener
    let collectionGroupRef = firestore().collectionGroup('messages')
    if (user?.accessType === 'admin') {
      collectionGroupRef = collectionGroupRef.where('provider_id', '==', user?.uid)
    }
    unsubscribeMessages = collectionGroupRef
      .orderBy("created", "desc")
      .limit(1)
      .onSnapshot(snap => {
        if (!snap.empty) {
          let parent_id = snap.docs?.[0].ref.parent.parent.id
          dispatch(getChatMessages({ ...snap.docs[0].data(), parent_id }))
        }
      });

    // Store unsubscribe function in ref
    unsubscribeMessagesRef.current = unsubscribeMessages;

    // Load initial chats
    (async () => {
      try {
        let ref = firestore().collection('chats')
        if (user?.accessType === 'admin') {
          ref = ref.where('provider_id', '==', user?.uid)
        }
        // Filter by labels if selected (multiple labels)
        if (chatsFilter?.labelIds && chatsFilter.labelIds.length > 0) {
          ref = ref.where('labels', 'array-contains-any', chatsFilter.labelIds)
        }
        if (chatsFilter?.unread) {
          ref = ref.where('newCount', '>', 0)
        } else {
          ref = ref.orderBy("updated", "desc")
        }
        let snap = await ref
          .limit(30) // Reduced from 100 to 30 for better initial performance
          .get()
        setLastVisibleChat(snap.docs[snap.docs.length - 1])
        setHasMoreChats(snap.docs.length >= 30)
        getInitialData(snap)
      } catch (error) {
        console.error('Error loading chats:', error);
      }
    })()

    // Cleanup function
    return () => {
      if (unsubscribeMessages) {
        console.log('Unsubscribing from messages listener');
        unsubscribeMessages();
        unsubscribeMessagesRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [user?.uid, user?.accessType, chatsFilter?.unread, chatsFilter?.labelIds])

  // Optimize: Listen to chat updates - only when needed
  useEffect(() => {
    if (!user?.uid) return;

    let ref = firestore().collection('chats')
    if (user?.accessType === 'admin') {
      ref = ref.where('provider_id', '==', user?.uid)
    }
    // Apply same filters as initial load
    if (chatsFilter?.labelIds && chatsFilter.labelIds.length > 0) {
      ref = ref.where('labels', 'array-contains-any', chatsFilter.labelIds)
    }
    if (chatsFilter?.unread) {
      ref = ref.where('newCount', '>', 0)
    } else {
      ref = ref.orderBy('updated', 'desc')
    }

    const unsubscribe = ref.limit(1).onSnapshot(async snap => {
      if (!snap.empty) {
        getInitialData(snap)
      }
    }, (error) => {
      console.error('Error in chat updates listener:', error);
    })

    // Store unsubscribe function in ref
    unsubscribeChatsRef.current = unsubscribe;

    return () => {
      if (unsubscribe) {
        console.log('Unsubscribing from chats listener');
        unsubscribe();
        unsubscribeChatsRef.current = null;
      }
    }
    // eslint-disable-next-line
  }, [user?.uid, user?.accessType, chatsFilter?.unread, chatsFilter?.labelIds])

  // Cleanup all listeners on component unmount
  useEffect(() => {
    return () => {
      console.log('Chat component unmounting - cleaning up all listeners');
      if (unsubscribeMessagesRef.current) {
        unsubscribeMessagesRef.current();
        unsubscribeMessagesRef.current = null;
      }
      if (unsubscribeChatsRef.current) {
        unsubscribeChatsRef.current();
        unsubscribeChatsRef.current = null;
      }
      // Cancel any pending debounced functions
      if (debounce_fun && debounce_fun.cancel) {
        debounce_fun.cancel();
      }
    };
  }, [debounce_fun]);
  const onInfiniteLoad = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingMore) {
      console.log('onInfiniteLoad: Already loading, skipping');
      return;
    }

    if (!selectedUser?.id) {
      console.log('onInfiniteLoad: No selectedUser.id');
      return;
    }

    // Get the latest contact from allContacts to ensure we have the most recent lastVisible
    const latestContact = allContacts?.find(contact => contact.id === selectedUser.id);
    if (!latestContact) {
      console.log('onInfiniteLoad: Contact not found in allContacts');
      return;
    }

    if (!latestContact?.lastVisible) {
      console.log('onInfiniteLoad: No lastVisible, stopping infinite scroll');
      // Update to stop showing loader
      let updateUser = { ...latestContact, isInfiniteLoading: false };
      let updateContacts = allContacts?.map(contact =>
        contact.id === selectedUser.id ? updateUser : contact
      );
      dispatch(getContacts(updateContacts?.sort(compareContacts)));
      return;
    }

    setIsLoadingMore(true);

    try {
      let ref = firestore().collection('chats').doc(selectedUser.id).collection('messages')
      if (user?.accessType === 'admin') {
        ref = ref.where('provider_id', '==', user?.uid)
      }

      let snap = await ref
        .orderBy("created", "desc")
        .startAfter(latestContact.lastVisible)
        .limit(10).get()

      console.log('onInfiniteLoad: Loaded', snap.docs.length, 'messages');

      if (snap.empty || snap.docs.length === 0) {
        console.log('onInfiniteLoad: No more messages, stopping and updating isInfiniteLoading to false');

        // Update contact to stop showing loader
        let updateUser = { ...latestContact, isInfiniteLoading: false };
        let updateContacts = allContacts?.map(contact =>
          contact.id === selectedUser.id ? updateUser : contact
        );

        dispatch(getContacts(updateContacts?.sort(compareContacts)));
        setIsLoadingMore(false);
        return;
      }

      // Batch all message dispatches together
      const newMessages = snap.docs.map((d) => {
        let parent_id = d.ref.parent.parent.id
        return { ...d.data(), parent_id }
      });

      const lastVisible = snap.docs[snap.docs.length - 1];
      const hasMore = snap.docs.length >= 10;
      let updateUser = { ...latestContact, lastVisible: lastVisible, isInfiniteLoading: hasMore };

      console.log('onInfiniteLoad: Updating contact with new lastVisible, hasMore:', hasMore);

      // Optimize contacts update - use map instead of reduce
      let updateContacts = allContacts?.map(contact =>
        contact.id === selectedUser.id ? updateUser : contact
      );

      // Batch all dispatches together
      batch(() => {
        newMessages.forEach(msg => dispatch(getChatMessages(msg)));
        dispatch(getContacts(updateContacts?.sort(compareContacts)));
      });

      console.log('onInfiniteLoad: Update complete');
    } catch (error) {
      console.error('Error loading more messages:', error);
      // On error, stop loading to prevent infinite retry
      const currentContact = allContacts?.find(contact => contact.id === selectedUser.id);
      if (currentContact) {
        let updateUser = { ...currentContact, isInfiniteLoading: false };
        let updateContacts = allContacts?.map(contact =>
          contact.id === selectedUser.id ? updateUser : contact
        );
        dispatch(getContacts(updateContacts?.sort(compareContacts)));
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [allContacts, dispatch, selectedUser, user, isLoadingMore])

  const onLoadMoreContacts = useCallback(async () => {
    if (!lastVisibleChat) return;

    try {
      let ref = firestore().collection('chats')
      if (user?.accessType === 'admin') {
        ref = ref.where('provider_id', '==', user?.uid)
      }
      // Filter by labels if selected (multiple labels)
      if (chatsFilter?.labelIds && chatsFilter.labelIds.length > 0) {
        ref = ref.where('labels', 'array-contains-any', chatsFilter.labelIds)
      }
      if (chatsFilter?.unread) {
        ref = ref.where('newCount', '>', 0)
      } else {
        ref = ref.orderBy("updated", "desc")
      }

      let snap = await ref
        .startAfter(lastVisibleChat)
        .limit(30) // Reduced from 100 to 30 for better performance
        .get()

      if (snap.empty) {
        setHasMoreChats(false);
        return;
      }

      setLastVisibleChat(snap.docs[snap.docs.length - 1])
      setHasMoreChats(snap.docs.length >= 30)
      getInitialData(snap)
    } catch (error) {
      console.error('Error loading more contacts:', error);
      setHasMoreChats(false);
    }

    // eslint-disable-next-line
  }, [user, lastVisibleChat, chatsFilter, getInitialData])

  const handleChatInputPress = useCallback(async e => {
    if (e.key === "Enter" || e.type === "click") {
      if (disableSendButton === true || !messageInput?.trim() || !selectedUser?.id) {
        return;
      }

      setDisableSendButton(true)
      const message = messageInput.trim();
      const senderName = user?.accessType === 'admin' ? Object.values(provider)?.[0]?.name : (user?.displayName || "Chill");

      // Optimistically add message to UI
      dispatch(getChatMessages({
        created: Date.now(),
        from: senderName,
        role: user?.accessType,
        sender: user.uid,
        id: '===spam',
        status: "Created",
        text: {
          body: message,
        },
        type: "text",
        parent_id: selectedUser.id
      }))
      setMessageInput("")

      try {
        let addMessage = functions().httpsCallable("SendWhatsappMessage");
        await addMessage({
          text: message,
          phoneNumber: selectedUser.id,
          from: senderName
        });
        dispatch(deleteChatMessage({ id: '===spam', parent_id: selectedUser.id }))
        setDisableSendButton(false)
      } catch (error) {
        console.error('Error sending message:', error)
        dispatch(deleteChatMessage({ id: '===spam', parent_id: selectedUser.id }))
        createNotification("error", error.message)
        setDisableSendButton(false)
      }
    }
  }, [messageInput, selectedUser?.id, disableSendButton, dispatch, user?.uid, user?.accessType, user?.displayName, provider])
  // Memoize the expensive addDateMessages function
  const addDateMessages = useCallback((messages) => {
    const sortedMessages = [...messages].sort((a, b) => a.created - b.created);
    const messagesWithDates = [];
    let currentDate = null;

    for (const message of sortedMessages) {
      const messageDate = moment(message.created).format('MMM DD, YYYY');

      if (messageDate !== currentDate) {
        messagesWithDates.push({ type: 'date', content: messageDate, created: moment(message.created).startOf('day').valueOf() });
        currentDate = messageDate;
      }

      messagesWithDates.push(message);
    }

    return messagesWithDates;
  }, []);
  // Memoize the current chat messages to avoid re-sorting on every render
  const currentChatMessages = useMemo(() => {
    return Object.values(messages?.[selectedUser?.id] || {});
  }, [messages, selectedUser?.id]);

  // Memoize sorted messages with dates
  const messagesWithDates = useMemo(() => {
    if (currentChatMessages.length) {
      return addDateMessages(currentChatMessages).sort((a, b) => b?.created - a?.created);
    }
    return [];
  }, [currentChatMessages, addDateMessages]);

  useEffect(() => {
    if (selectedUser && currentChatMessages.length) {
      let chatMessages = [...currentChatMessages].sort((msg1, msg2) => msg1?.created - msg2?.created);
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (lastMessage?.template_name === 'create_new_session') {
        setDisableSendButton({ text: 'Waiting For Customer Reply' })
      } else {
        setDisableSendButton(false)
      }
    }
  }, [selectedUser, currentChatMessages])

  // Scroll to bottom when switching chats
  useEffect(() => {
    if (selectedUser && scrollableDivRef.current) {
      // With column-reverse, bottom is at scrollTop = 0
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        if (scrollableDivRef.current) {
          scrollableDivRef.current.scrollTop = 0;
          previousScrollTopRef.current = 0;
        }
      }, 100);
    }
    // eslint-disable-next-line
  }, [selectedUser?.id])

  // Preserve scroll position when loader appears/disappears
  useEffect(() => {
    const container = scrollableDivRef.current;
    if (!container || !selectedUser) return;

    // Save current scroll position before state change
    const savedScrollTop = previousScrollTopRef.current;

    // Use double RAF to ensure DOM has updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (container && savedScrollTop !== undefined && savedScrollTop > 0) {
          // Restore scroll position if we were scrolled up (not at bottom)
          // In column-reverse, scrollTop > 0 means we're scrolled up from bottom
          const currentScrollTop = container.scrollTop;
          // Only restore if scroll position changed significantly (more than 10px)
          if (Math.abs(currentScrollTop - savedScrollTop) > 10) {
            container.scrollTop = savedScrollTop;
          }
        }
      });
    });
  }, [selectedUser?.isInfiniteLoading, selectedUser])

  // Handle back navigation on mobile
  const handleBackToChats = useCallback(() => {
    navigateToUser(null);
  }, [navigateToUser]);

  // Determine what to show on mobile
  const showChatMenu = isMobile ? !selectedUser : true;
  const showChatDetails = isMobile ? !!selectedUser : true;

  return (
    <Fragment>
      <Row className="app-row survey-app">
        {showChatMenu && (
          <Colxx xxs="12" >
            <ChatApplicationMenu
              onLoadMoreContacts={onLoadMoreContacts}
              hasMoreChats={hasMoreChats}
              handleSearchContact={handleSearchContact}
              searchKey={searchKey}
              navigateToUser={navigateToUser}
              mobileFullscreen={isMobile && !selectedUser}
              selectedUserId={userIdFromUrl}
            />
          </Colxx>
        )}
        {showChatDetails && (
          <Colxx xxs="12" className="chat-app-main p-0">
            {selectedUser && (
              <div className="chat-content-wrapper">
                <ChatHeading
                  name={selectedUser?.name}
                  selectedUser={selectedUser}
                  thumb={'selectedUser?.thumb'}
                  wa_id={selectedUser?.wa_id}
                  onBackToChats={handleBackToChats}
                  isMobile={isMobile}
                />
              </div>
            )}

            {currentChatMessages.length ?
              <div
                ref={scrollableDivRef}
                id="scrollableDiv"
                className="chat-messages-container"
                style={{
                  display: 'flex',
                  flexDirection: 'column-reverse',
                }}
                onScroll={(e) => {
                  // Save scroll position as user scrolls
                  previousScrollTopRef.current = e.target.scrollTop;
                }}
              >
                <InfiniteScroll
                  dataLength={currentChatMessages.length}
                  next={onInfiniteLoad}
                  style={{ display: 'flex', flexDirection: 'column-reverse', padding: '20px', }}
                  inverse={true}
                  className="hide-scrollbar"
                  hasMore={selectedUser?.isInfiniteLoading}
                  scrollableTarget="scrollableDiv"
                  loader={
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '20px 0',
                      width: '100%',
                      height: '70px',
                      flexShrink: 0,
                      boxSizing: 'border-box'
                    }}>
                      <div style={{
                        display: 'inline-block',
                        width: '30px',
                        height: '30px',
                        border: '2px solid rgba(145, 145, 145, 0.2)',
                        borderRadius: '50%',
                        borderTopColor: '#145388',
                        animation: 'spin 1s ease-in-out infinite',
                        WebkitAnimation: 'spin 1s ease-in-out infinite'
                      }} />
                    </div>
                  }
                  endMessage={
                    <p style={{ textAlign: "center" }}>
                      <b>No more messages</b>
                    </p>
                  }>

                  {messagesWithDates.map((item, index, array) => {
                    if (item.type === 'date') {
                      return (
                        <div key={`date-${item.created}`} style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                          <hr style={{ flex: 1 }} />
                          <div style={{ margin: '0 10px', fontSize: '0.8rem', color: 'gray' }}>{item.content}</div>
                          <hr style={{ flex: 1 }} />
                        </div>
                      );
                    }
                    const sender = allContacts?.find(x => x.id === item.from);
                    const previousMessage = array[index - 1];
                    const sameSender = previousMessage && previousMessage.from === item.from;
                    return <MessageCard
                      key={item.id || index}
                      sender={sender}
                      item={item}
                      currentUserid={user?.uid}
                      sameSender={sameSender}
                    />
                  })}
                </InfiniteScroll>
              </div>
              :
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px', width: '100%' }}>{selectedUser ? "No Messages" : "No chat selected"}</div>
            }
            {selectedUser && <SaySomething
              placeholder={'Type Something...'}
              messageInput={messageInput}
              setMessageInput={setMessageInput}
              handleChatInputPress={handleChatInputPress}
              handleSendButtonClick={handleChatInputPress}
              disableSendButton={disableSendButton}
            />}
          </Colxx>
        )}
      </Row>
    </Fragment>
  );
};

export default withRouter(Chat);

